import { createMemoryHistory, createRouter, createWebHistory, type Router } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { APP_DISPLAY_NAME, withAppName } from '@/config/app'


const PCLayout = () => import('@/layouts/Layout.vue')
const MobileLayout = () => import('@/layouts/MobileLayout.vue')


export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    alias: ['/index.html'],
    name: 'Index',
    component: () => import('@/views/web/Index.vue'),
    meta: {
      requiresAuth: false,
      title: APP_DISPLAY_NAME
    }
  },
  {
    path: '/agreement',
    alias: ['/agreement.html'],
    name: 'Agreement',
    component: () => import('@/components/agreement/UserAgreement.vue'),
    meta: {
      requiresAuth: false,
      title: '用户协议'
    }
  },
  {
    path: '/privacy',
    alias: ['/privacy.html'],
    name: 'Privacy',
    component: () => import('@/components/agreement/PrivacyPolicy.vue'),
    meta: {
      requiresAuth: false,
      title: '隐私政策'
    }
  },
  {
    path: '/download',
    name: 'Download',
    component: () => import('@/views/download/Download.vue'),
    meta: {
      requiresAuth: false,
      title: `${APP_DISPLAY_NAME}-下载`
    }
  },
  {
    path: '/terms',
    alias: ['/terms.html'],
    name: 'PayPolicy',
    component: () => import('@/components/agreement/PayPolicy.vue'),
    meta: {
      requiresAuth: false,
      title: '付费服务协议'
    }
  },
  {
    path: '/usage-policy',
    alias: ['/usage-policy.html'],
    name: 'UsagePolicy',
    component: () => import('@/components/agreement/UsagePolicy.vue'),
    meta: {
      requiresAuth: false,
      title: '会员与积分规则说明'
    }
  },
  {
    path: '/user-manual',
    alias: ['/user-manual.html'],
    name: 'UserManual',
    component: () => import('@/views/manual/UserManual.vue'),
    meta: {
      requiresAuth: false,
      title: withAppName('使用手册')
    }
  },
  {
    path: '/children',
    alias: ['/children.html'],
    name: 'Children',
    component: () => import('@/components/agreement/ChildrenPolicy.vue'),
    meta: {
      requiresAuth: false,
      title: '儿童信息保护说明'
    }
  },
  {
    path: '/algorithm',
    alias: ['/algorithm.html'],
    name: 'AlgorithmNotice',
    component: () => import('@/components/agreement/AlgorithmNotice.vue'),
    meta: {
      requiresAuth: false,
      title: '模型及算法备案公示说明'
    }
  },
  {
    path: '/thirdSDK',
    alias: ['/thirdSDK.html'],
    name: 'ThirdSDK',
    component: () => import('@/components/agreement/ThirdSDK.vue'),
    meta: {
      requiresAuth: false,
      title: '第三方SDK目录'
    }
  },

  {
    path: '/ateA',
    name: 'ActionSse',
    component: () => import('@/views/test/DocumentView.vue'),
    meta: {
      requiresAuth: false,
      title: '第三方SDK目录'
    }
  },



  // ============ 主应用页面 ============ begin ========================
  {
    path: '/zhuli',
    name: 'Assistant',
    // 动态组件，根据设备返回不同布局
    component: () => {
      // 注意：这里不能直接使用 composable，需要直接检测
      const isMobile = window.innerWidth < 768
      return isMobile ? MobileLayout() : PCLayout()
    },
    redirect: '/zhuli/home',
    meta: {
      requiresAuth: false,
      title: APP_DISPLAY_NAME
    },
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/home/Home.vue'),
        meta: {
          requiresAuth: false,
          title: withAppName('首页'),
          keepAlive: true
        }
      }, {
        path: 'ai-create',
        name: 'AICreate',
        component: () => import('@/views/home/AICreate.vue'),
        meta: {
          requiresAuth: false,
          title: withAppName('写作工具'),
          keepAlive: true
        }
      }, {
        path: 'action',
        name: 'Action',
        component: () => import('@/views/action/WritingView.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('写作'),
          hideTabBar: true
        }
      }
      , {
        path: 'history',
        name: 'History',
        component: () => import('@/views/history/History.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('历史'),
          keepAlive: true
        }
      },
      {
        path: 'article',
        name: 'Article',
        component: () => import('@/views/article/Article.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('文库'),
          keepAlive: true
        }
      },
      {
        path: 'article/detail',
        name: 'ArticleDetail',
        component: () => import('@/views/article/ArticleDetail.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('文库详情')
        }
      },
      {
        path: 'collection',
        name: 'Collection',
        component: () => import('@/views/collection/Collection.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('我的收藏'),
          keepAlive: true
        }
      }, {
        path: 'my',
        name: 'My',
        component: () => import('@/views/my/My.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('我的'),
          keepAlive: true
        }
      }, {
        path: 'my-document',
        name: 'MyDocumentList',
        component: () => import('@/views/my/MyDocumentList.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('我的文档'),
          hideTabBar: true,
          keepAlive: true
        }
      }, {
        path: 'my-document/:messageId',
        name: 'MyDocumentDetail',
        component: () => import('@/views/my/MyDocumentDetail.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('文档详情'),
          hideTabBar: true
        }
      }, {
        path: 'purchase-record',
        name: 'PurchaseRecord',
        component: () => import('@/views/my/PurchaseRecord.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('购买记录'),
          hideTabBar: true
        }
      }, {
        path: 'point-consume-record',
        name: 'PointConsumeRecord',
        component: () => import('@/views/my/PointConsumeRecord.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('积分消耗'),
          hideTabBar: true
        }
      }, {
        path: 'my-voucher',
        name: 'MyVoucher',
        component: () => import('@/views/my/MyVoucher.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('我的优惠券'),
          hideTabBar: true
        }
      }, {
        path: 'sys-message',
        name: 'SysMessage',
        component: () => import('@/views/my/SysMessageList.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('系统消息'),
          hideTabBar: true
        }
      }, {
        path: 'about',
        name: 'About',
        component: () => import('@/views/my/About.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('关于我们'),
          hideTabBar: true
        }
      }, {
        path: 'setting',
        name: 'Setting',
        component: () => import('@/views/my/Setting.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('设置'),
          hideTabBar: true
        }
      }, {
        path: 'setting/other',
        name: 'OtherSetting',
        component: () => import('@/views/my/OtherSetting.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('其他设置'),
          hideTabBar: true
        }
      }, {
        path: 'customer',
        name: 'Customer',
        component: () => import('@/views/customer/Customer.vue'),
        meta: {
          requiresAuth: true,
          title: withAppName('客服')
        }
      },
    ]
  },
  // ============ 主应用页面 ============ end ========================

]

// 检测是否为移动端
const isMobileDevice = () => {
  const ua = navigator.userAgent.toLowerCase()
  const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone', 'mobile']
  const isMobileUA = mobileKeywords.some(keyword => ua.includes(keyword))
  const isMobileWidth = window.innerWidth <= 768
  return isMobileUA || isMobileWidth
}


export function installRouterGuards(router: Router) {
  router.beforeEach((to, from, next) => {
    const authStore = useAuthStore()

    // 设置页面标题
    if (to.meta.title && typeof document !== 'undefined') {
      document.title = `${to.meta.title}`
    }

    // 1. 移动端助理页跳转逻辑
    // const isZhuliPath = to.path.includes('/zhuli')
    // if (isZhuliPath && typeof window !== 'undefined') {
    //   if (isMobileDevice()) {
    //     next('/download')
    //     return
    //   }
    // }

    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      authStore.openLoginDialog()
      next('/zhuli')
      return // 添加 return，避免继续执行
    }
    next()
  })
}

const router = createRouter({
  history: typeof window !== 'undefined' ? createWebHistory() : createMemoryHistory(),
  routes
})

installRouterGuards(router)

export default router